// The words on an upload's progress row (owner, 2026-09-03).
//
// Split out of `UploadScreen` when the progress moved to its own toast: the
// app's test suite runs on `environment: 'node'` with no renderer, so wording
// that lives inside a component cannot be asserted, and this wording is the
// whole point of the change. Nothing here touches React.
//
// NOTHING IS A GUESS ABOUT SERVER STATE. Each label names a phase the server
// actually published (`uploads.processing_phase`), and `unknown` says plainly that the app stopped being able
// to see the upload — not that anything went wrong with it.
import type { UploadProgress, UploadStage } from './hooks'

/** The three client-side send states, then the server's own stages. */
export type DisplayStage = 'pending' | 'uploading' | 'send_failed' | UploadStage

/**
 * THE OWNER'S OWN WORDS for the four pipeline phases (2026-09-03), replacing a
 * single "Processing…" that covered all of them:
 *
 *   Checking pages -> Extracting content -> Raising flags -> Finishing up
 *
 * They are deliberately about the DOCUMENT rather than the machinery — an
 * uploader watching this wants to know what is being done to their invoice, not
 * which Lambda holds the message.
 */
export function stageLabel(stage: DisplayStage, progress: UploadProgress | undefined): string {
  switch (stage) {
    case 'pending':
      return 'Ready to send'
    case 'uploading':
      return 'Uploading…'
    case 'received':
      // Registered, and nothing has picked it up yet. A FACT since the phase
      // column exists — it used to be an inference from elapsed time.
      return 'Queued'
    case 'splitting':
      return 'Checking pages'
    case 'extracting':
      return 'Extracting content'
    case 'matching':
      return 'Raising flags'
    case 'finishing':
      return 'Finishing up'
    case 'parsed': {
      const n = progress?.recordCount
      // "READY", not "found" (owner, 2026-09-03). By the time this shows, every
      // invoice has been through matching and is sitting in a queue waiting for
      // a person — which is what the uploader actually wants to know. "Found"
      // was also literally true a stage too early, before flags were raised.
      if (n === null || n === undefined) return 'Done'
      if (n === 0) return 'Done — no invoices found in this document'
      return n === 1 ? 'Done — 1 invoice ready' : `Done — ${n} invoices ready`
    }
    // No upload is ever refused (owner, 2026-08-10): a document that is not a
    // bill still becomes a record a person archives. `Failed` is the one bad
    // ending, and it carries no code (code review A-06, 2026-10-03).
    case 'failed':
      return 'Extraction failed'
    case 'unknown':
      return 'Processing continues in the background — check the queues shortly.'
    case 'send_failed':
      return 'Not sent'
  }
}

/**
 * Stage ordinals, not a measured percentage — the words are the real signal and
 * the bar only shows the row is moving. Terminal stages get no bar.
 *
 * The four phases are evenly spaced because nothing here knows how long any of
 * them takes; a weighting would be a claim about timing this app cannot make.
 */
export const STAGE_FRACTION: Partial<Record<DisplayStage, number>> = {
  uploading: 0.1,
  received: 0.2,
  splitting: 0.4,
  extracting: 0.6,
  matching: 0.8,
  finishing: 0.95,
}

/** Whether the row is still moving — drives the spinner and the progress bar. */
export function isInFlight(stage: DisplayStage): boolean {
  return STAGE_FRACTION[stage] !== undefined
}

/** Whether the row has stopped, however it stopped. Drives the toast's timer. */
export function isSettled(stage: DisplayStage): boolean {
  return stage === 'parsed' || stage === 'failed'
    || stage === 'unknown' || stage === 'send_failed'
}
